import { brusselsToday } from '../../../utils/dateFormat';

// Month / year arithmetic for the reconciliation pickers. A month is a Europe/Brussels calendar month
// (`YYYY-MM`), handled by string arithmetic only — no Date object, so no timezone can shift it. The backend
// refuses a future month or year and a year before 2000; the pickers never offer one.

export const MIN_YEAR = 2000;
const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const YEAR_RE = /^\d{4}$/;

/** The current Brussels month, `YYYY-MM`. */
export function currentMonth(now: number = Date.now()): string {
  return brusselsToday(now).slice(0, 7);
}

/** The current Brussels year. */
export function currentYear(now: number = Date.now()): number {
  return Number(brusselsToday(now).slice(0, 4));
}

/** `month` shifted by `delta` months (`"2026-01"`, -1 → `"2025-12"`). */
export function shiftMonth(month: string, delta: number): string {
  const match = MONTH_RE.exec(month);
  if (match === null) {
    return month;
  }
  const index = Number(match[1]) * 12 + (Number(match[2]) - 1) + delta;
  const year = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return `${String(year).padStart(4, '0')}-${String(m).padStart(2, '0')}`;
}

/** The month the accountant usually reconciles: the previous Brussels month. */
export function defaultMonth(now: number = Date.now()): string {
  return shiftMonth(currentMonth(now), -1);
}

/** A well-formed month between January 2000 and the current Brussels month. */
export function isSelectableMonth(value: unknown, now: number = Date.now()): value is string {
  return typeof value === 'string' && MONTH_RE.test(value) && value >= `${MIN_YEAR}-01` && value <= currentMonth(now);
}

/** A year between 2000 and the current Brussels year. */
export function isSelectableYear(value: unknown, now: number = Date.now()): boolean {
  const s = typeof value === 'number' ? String(value) : value;
  return typeof s === 'string' && YEAR_RE.test(s) && Number(s) >= MIN_YEAR && Number(s) <= currentYear(now);
}

/** The months offered by the month picker, newest first: the last `count` months, plus `selected` if older. */
export function monthOptions(selected: string, now: number = Date.now(), count: number = 36): string[] {
  const latest = currentMonth(now);
  const months: string[] = [];
  for (let i = 0; i < count; i++) {
    const m = shiftMonth(latest, -i);
    if (m < `${MIN_YEAR}-01`) {
      break;
    }
    months.push(m);
  }
  if (!months.includes(selected) && isSelectableMonth(selected, now)) {
    months.push(selected);
  }
  return months;
}

/** The years offered by the year picker, newest first (current year down to 2000). */
export function yearOptions(now: number = Date.now()): number[] {
  const years: number[] = [];
  for (let y = currentYear(now); y >= MIN_YEAR; y--) {
    years.push(y);
  }
  return years;
}
